import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Gavel, ScrollText } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import {
  useDecideLatenessCase,
  useRecordLatenessHearing,
  useRecordLatenessNotice,
  useRecordLatenessProcedure,
  useRecordLatenessResponse,
  useRecordPaperWarning,
  useRecordWarningRefusal,
  useSignWarningUpload,
} from '../../hooks/backend/lateness'
import { writeErrorMessage } from '../../utils/writeError'
import {
  NOTICE_METHODS,
  STATUS_LABEL,
  STEP_LABEL,
  WARNING_STATUS_LABEL,
  decisionChoices,
  hearingOutcomes,
  type LatenessCase,
  type LatenessPage,
} from '../../utils/lateness'

// One lateness case, and the only place its steps are recorded.
//
// THE ORDER IS THE DATABASE'S. The record shown to the driver, his
// explanation, the clause 27.4 steps where they are needed, then the decision.
// Each function refuses a step out of order and says what is missing, and that
// sentence is shown as it comes. The form below offers only the step the case
// is on.
//
// THE CHOICES ARE BOUNDED BY THE LADDER. Nothing heavier than the step the
// ladder gives. A final written warning and dismissal are only ever a hearing's
// outcome, recorded by the hearing chair.

const today = () => new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)

function Box({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'warn' | 'ok' }) {
  const cls =
    tone === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
      : tone === 'ok'
        ? 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
        : 'border-border bg-muted/40 text-foreground'
  return <div className={`rounded-md border p-3 text-sm ${cls}`}>{children}</div>
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function LatenessCaseDialog({
  c,
  page,
  onClose,
  onChanged,
}: {
  c: LatenessCase | null
  page: LatenessPage
  onClose: () => void
  onChanged: () => void
}) {
  const notice = useRecordLatenessNotice()
  const response = useRecordLatenessResponse()
  const procedure = useRecordLatenessProcedure()
  const decide = useDecideLatenessCase()
  const hearing = useRecordLatenessHearing()
  const refusal = useRecordWarningRefusal()
  const signUpload = useSignWarningUpload()
  const paper = useRecordPaperWarning()
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState<Record<string, string>>({})
  const [checks, setChecks] = useState<Record<string, boolean>>({})
  const [file, setFile] = useState<File | null>(null)
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  const tick = (k: string) => (e: { target: { checked: boolean } }) => setChecks((x) => ({ ...x, [k]: e.target.checked }))

  if (!c) return null

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      toast.success(label)
      setF({})
      setChecks({})
      setFile(null)
      onChanged()
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const proc = () => ({
    allegation_given_on: f.alleg_on ?? '',
    allegation_method: f.alleg_method ?? '',
    meeting_on: f.meeting_on ?? '',
    chair_person_id: f.chair ?? '',
    representation_offered: checks.rep ?? false,
    representative: f.representative ?? '',
    interpreter_offered: checks.interp ?? false,
    interpreter: f.interpreter ?? '',
    driver_response: f.driver_response ?? '',
    outcome_note: f.outcome_note ?? '',
  })

  const staff = page.options.staff
  const fair = c.fairness
  const needsProcedure = c.step !== 'verbal_warning'
  const hasProcedure = !!c.procedure

  const procedureFields = (withMeeting: boolean) => (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Allegation given in writing on">
        <Input type="date" value={f.alleg_on ?? ''} onChange={set('alleg_on')} />
      </Field>
      <Field label="How it was given">
        <NativeSelect value={f.alleg_method ?? ''} onChange={set('alleg_method')}>
          <option value="">Choose</option>
          {NOTICE_METHODS.filter((m) => m.value !== 'driver_page').map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </NativeSelect>
      </Field>
      {withMeeting && (
        <Field label="Meeting held on" hint="At least 2 working days after the allegation.">
          <Input type="date" value={f.meeting_on ?? ''} onChange={set('meeting_on')} />
        </Field>
      )}
      <Field label="Independent chair" hint="Not his own Driver Servant, and not you.">
        <NativeSelect value={f.chair ?? ''} onChange={set('chair')}>
          <option value="">Choose</option>
          {staff.map((s) => (
            <option key={s.person_id} value={s.person_id}>
              {s.name}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={checks.rep ?? false} onChange={tick('rep')} /> A representative was offered
      </label>
      <Input placeholder="Representative, if he brought one" value={f.representative ?? ''} onChange={set('representative')} />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={checks.interp ?? false} onChange={tick('interp')} /> An interpreter was offered
      </label>
      <Input placeholder="Interpreter, if one was used" value={f.interpreter ?? ''} onChange={set('interpreter')} />
      <div className="sm:col-span-2">
        <Field label="What he said at the meeting">
          <Textarea value={f.driver_response ?? ''} onChange={set('driver_response')} placeholder="Or that he did not attend" />
        </Field>
      </div>
    </div>
  )

  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {c.driver}, {c.day}
            <Chip>{c.ref}</Chip>
            <Chip>{STATUS_LABEL[c.status]}</Chip>
          </DialogTitle>
          <DialogDescription>
            The ladder gives {c.step_label}. That is what the rules would give, not a decision.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <ScrollText className="h-4 w-4" /> The record
            </h3>
            <p className="text-sm">{c.allegation}</p>
            <ul className="space-y-1 text-sm">
              {c.evidence.map((e) => (
                <li key={e.incoming_shift_id} className="rounded-md border border-border p-2">
                  <span className="font-medium">
                    {e.shift_date} {e.handover_time}, {e.plate}
                  </span>
                  : the {e.role} driver, {e.minutes_late} min late. Partner: {e.partner ?? '—'}.{' '}
                  {e.form_bucket ? `Form: ${e.form_bucket}. ` : 'No form. '}
                  {e.cartrack ? `Cartrack: ${e.cartrack}. ` : ''}
                  {e.review_note ? `Ruling: ${e.review_note}. ` : ''}
                  {e.borderline && <Chip>Borderline, 15 to 30 bucket</Chip>}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Fairness checks</h3>
            {!fair ? (
              <Box tone="warn">The fairness checks were not stored on this case.</Box>
            ) : (
              <div className="grid gap-2 sm:grid-cols-3">
                <Box tone={fair.partner_late_same_week.found ? 'warn' : 'muted'}>
                  <p className="font-medium">Partner also late that week</p>
                  {fair.partner_late_same_week.found
                    ? fair.partner_late_same_week.rows.map((r, i) => (
                        <p key={i}>
                          {r.date} {r.handover}: {r.partner}, {r.minutes} min
                        </p>
                      ))
                    : 'No'}
                </Box>
                <Box tone={fair.vehicle_breakdown_or_service.found ? 'warn' : 'muted'}>
                  <p className="font-medium">Car in breakdown or service</p>
                  {fair.vehicle_breakdown_or_service.found ? fair.vehicle_breakdown_or_service.rows.map((r, i) => <p key={i}>{r}</p>) : 'No'}
                </Box>
                <Box tone={fair.fleetly_paid_late.found !== false ? 'warn' : 'muted'}>
                  <p className="font-medium">Fleetly paid him late</p>
                  {fair.fleetly_paid_late.found === null
                    ? fair.fleetly_paid_late.note
                    : fair.fleetly_paid_late.found
                      ? fair.fleetly_paid_late.rows.map((r, i) => <p key={i}>Released {r.released_on}</p>)
                      : 'No'}
                </Box>
              </div>
            )}
          </section>

          {c.status === 'raised' && (
            <section className="space-y-3 rounded-md border border-border p-3">
              <h3 className="text-sm font-semibold">1. Give him the record in writing</h3>
              {!page.switches.messages_send && (
                <Box tone="warn">Lateness emails are switched off, so he has not been told. Give him the record, then record it here.</Box>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Given on">
                  <Input type="date" value={f.notice_on ?? ''} max={today()} onChange={set('notice_on')} />
                </Field>
                <Field label="How">
                  <NativeSelect value={f.method ?? ''} onChange={set('method')}>
                    <option value="">Choose</option>
                    {NOTICE_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
              <Textarea placeholder="Anything worth noting (optional)" value={f.note ?? ''} onChange={set('note')} />
              <Button
                disabled={busy}
                onClick={() =>
                  run('Recorded. His 2 working days have started.', () =>
                    notice.trigger({ case_id: c.id, notice_on: f.notice_on, method: f.method, note: f.note ?? null }),
                  )
                }
              >
                Record that he was given the record
              </Button>
            </section>
          )}

          {c.status === 'notice_issued' && (
            <section className="space-y-3 rounded-md border border-border p-3">
              <h3 className="text-sm font-semibold">2. His explanation</h3>
              <p className="text-sm text-muted-foreground">
                Given the record on {c.notice_sent} ({c.notice_method?.replace(/_/g, ' ')}). He has until the end of {c.response_due}.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="He explained on">
                  <Input type="date" value={f.responded_on ?? ''} max={today()} onChange={set('responded_on')} />
                </Field>
              </div>
              <Textarea placeholder="What he said, in his words as far as you can" value={f.response ?? ''} onChange={set('response')} />
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={busy}
                  onClick={() =>
                    run('Explanation recorded.', () =>
                      response.trigger({ case_id: c.id, responded_on: f.responded_on, response: f.response, no_response: false }),
                    )
                  }
                >
                  Record his explanation
                </Button>
                {!c.response_window_open && (
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => run('Recorded that no explanation came.', () => response.trigger({ case_id: c.id, no_response: true }))}
                  >
                    No explanation came
                  </Button>
                )}
              </div>
            </section>
          )}

          {c.status === 'response_received' && (
            <>
              <Box>
                <p className="font-medium">His explanation</p>
                <p className="whitespace-pre-wrap">{c.no_response ? 'None by the due date.' : c.response}</p>
              </Box>
              {needsProcedure && (
                <section className="space-y-3 rounded-md border border-border p-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <Gavel className="h-4 w-4" /> Clause 27.4 {hasProcedure ? '(recorded)' : ''}
                  </h3>
                  {hasProcedure ? (
                    <p className="text-sm text-muted-foreground">
                      Recorded: meeting {String(c.procedure?.meeting_on ?? '')}, chaired by {String(c.procedure?.chair ?? '')}.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      A written warning needs these steps first. For a final written warning or dismissal, refer it to a hearing and the chair records them.
                    </p>
                  )}
                  {c.step === 'written_warning' && (
                    <>
                      {procedureFields(true)}
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() => run('Clause 27.4 steps recorded.', () => procedure.trigger({ case_id: c.id, procedure: proc() }))}
                      >
                        Record the clause 27.4 steps
                      </Button>
                    </>
                  )}
                </section>
              )}
              <section className="space-y-3 rounded-md border border-border p-3">
                <h3 className="text-sm font-semibold">3. Your decision</h3>
                <Field label="Decision">
                  <NativeSelect value={f.decision ?? ''} onChange={set('decision')}>
                    <option value="">Choose</option>
                    {decisionChoices(c.step).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                {f.decision === 'excused' && (
                  <Field label="The clause 6.8 reason">
                    <NativeSelect value={f.excuse ?? ''} onChange={set('excuse')}>
                      <option value="">Choose</option>
                      {page.options.reasons
                        .filter((r) => r.valid)
                        .map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.label}
                          </option>
                        ))}
                    </NativeSelect>
                  </Field>
                )}
                <Textarea placeholder="Your reasons" value={f.reasons ?? ''} onChange={set('reasons')} />
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" checked={checks.fair ?? false} onChange={tick('fair')} />
                  I considered whether his partner was also late that week, whether the car was in a breakdown or service, and whether Fleetly paid
                  him late.
                </label>
                <Button
                  disabled={busy}
                  onClick={() =>
                    run('Decision recorded.', () =>
                      decide.trigger({
                        case_id: c.id,
                        decision: f.decision,
                        reasons: f.reasons,
                        excuse_reason: f.excuse ?? null,
                        fairness_considered: checks.fair === true,
                      }),
                    )
                  }
                >
                  Record the decision
                </Button>
              </section>
            </>
          )}

          {c.status === 'hearing_referred' && (
            <section className="space-y-3 rounded-md border border-border p-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                <Gavel className="h-4 w-4" /> Hearing outcome
              </h3>
              {!page.me.can_record_hearing ? (
                <Box tone="warn">The hearing chair, an Operational Servant or an administrator records the outcome.</Box>
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Held on">
                      <Input type="date" value={f.held_on ?? ''} max={today()} onChange={set('held_on')} />
                    </Field>
                    <Field label="Outcome">
                      <NativeSelect value={f.outcome ?? ''} onChange={set('outcome')}>
                        <option value="">Choose</option>
                        {hearingOutcomes(c.step).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </NativeSelect>
                    </Field>
                  </div>
                  {!hasProcedure && procedureFields(false)}
                  <Textarea placeholder="The hearing's reasons" value={f.reasons ?? ''} onChange={set('reasons')} />
                  <Button
                    disabled={busy}
                    onClick={() =>
                      run('Hearing outcome recorded.', () =>
                        hearing.trigger({
                          case_id: c.id,
                          held_on: f.held_on,
                          outcome: f.outcome,
                          reasons: f.reasons,
                          procedure: hasProcedure ? null : proc(),
                        }),
                      )
                    }
                  >
                    Record the outcome
                  </Button>
                </>
              )}
            </section>
          )}

          {(c.status === 'decided' || c.status === 'withdrawn') && (
            <Box tone="ok">
              <p className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="h-4 w-4" /> {c.outcome ? STEP_LABEL[c.outcome] ?? c.outcome.replace(/_/g, ' ') : 'Closed'}
              </p>
              <p>{c.outcome_reasons}</p>
            </Box>
          )}

          {(c.warnings ?? []).map((w) => (
            <section key={w.id} className="space-y-3 rounded-md border border-border p-3">
              <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                {STEP_LABEL[w.sanction] ?? w.sanction} given {w.given_on}
                <Chip>{WARNING_STATUS_LABEL[w.status]}</Chip>
              </h3>
              <p className="text-sm text-muted-foreground">
                In force from {w.given_on}, lapses {w.lapses_on ?? '—'}. He is asked to acknowledge it by {w.sign_due_on}.
              </p>
              {w.status === 'awaiting_signature' && (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">He refused, or did not sign in time</p>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <NativeSelect value={f[`kind_${w.id}`] ?? ''} onChange={set(`kind_${w.id}`)}>
                        <option value="">Which</option>
                        <option value="refused_in_person">Refused in person</option>
                        <option value="not_signed_in_time">Did not sign in 2 working days</option>
                      </NativeSelect>
                      <Input type="date" value={f[`ref_on_${w.id}`] ?? ''} max={today()} onChange={set(`ref_on_${w.id}`)} />
                      <NativeSelect value={f[`wit_${w.id}`] ?? ''} onChange={set(`wit_${w.id}`)}>
                        <option value="">Witness</option>
                        {staff
                          .filter((s) => s.person_id !== page.me.person_id)
                          .map((s) => (
                            <option key={s.person_id} value={s.person_id}>
                              {s.name}
                            </option>
                          ))}
                      </NativeSelect>
                    </div>
                    <Textarea placeholder="When and where it was given, and what he said" value={f[`rnote_${w.id}`] ?? ''} onChange={set(`rnote_${w.id}`)} />
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        run('Recorded. The witness has a task to confirm it.', () =>
                          refusal.trigger({
                            warning_id: w.id,
                            kind: f[`kind_${w.id}`],
                            refused_on: f[`ref_on_${w.id}`],
                            witness_person_id: f[`wit_${w.id}`],
                            note: f[`rnote_${w.id}`],
                          }),
                        )
                      }
                    >
                      Record with the witness
                    </Button>
                  </div>
                </>
              )}
              {(w.status === 'awaiting_signature' || w.status === 'refused_awaiting_witness') && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Paper fallback: a photo of the warning he signed on paper</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    <Input type="date" value={f[`paper_on_${w.id}`] ?? ''} max={today()} onChange={set(`paper_on_${w.id}`)} />
                  </div>
                  <Textarea placeholder="His written response on the paper, if any" value={f[`presp_${w.id}`] ?? ''} onChange={set(`presp_${w.id}`)} />
                  <Button
                    variant="outline"
                    disabled={busy || !file}
                    onClick={() =>
                      run('Signed paper copy filed.', async () => {
                        if (!file) throw new Error('Choose the photo first.')
                        if (file.size > 10 * 1024 * 1024) throw new Error('That photo is over 10 MB. Take it again at a lower size.')
                        const signed = (await signUpload.trigger({ warning_id: w.id, filename: file.name }, { skipCache: true })) as {
                          signedUrl?: string
                          path?: string
                        } | null
                        if (!signed?.signedUrl || !signed.path) throw new Error('No upload address came back.')
                        const put = await fetch(signed.signedUrl, {
                          method: 'PUT',
                          headers: file.type ? { 'Content-Type': file.type } : undefined,
                          body: file,
                        })
                        if (!put.ok) throw new Error(`Storage refused the photo (${put.status}).`)
                        await paper.trigger({
                          warning_id: w.id,
                          path: signed.path,
                          signed_on: f[`paper_on_${w.id}`],
                          response: f[`presp_${w.id}`] ?? null,
                        })
                      })
                    }
                  >
                    Upload and file it
                  </Button>
                </div>
              )}
            </section>
          ))}

          {(c.events ?? []).length > 0 && (
            <section className="space-y-1">
              <h3 className="text-sm font-semibold">What has happened</h3>
              <ol className="space-y-1 text-sm">
                {(c.events ?? []).map((e, i) => (
                  <li key={i} className="text-muted-foreground">
                    <span className="tabular-nums">{e.at}</span>
                    {e.by ? `, ${e.by}` : ''}: {e.detail}
                  </li>
                ))}
              </ol>
            </section>
          )}
          {!page.personLinked && (
            <Box tone="warn">
              <AlertTriangle className="mr-1 inline h-4 w-4" />
              Your login is not linked to a person record, so nothing you record here could carry your name.
            </Box>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
